import { HorizontalSlideInOut, StepComponent, Takeover, trackEvent, useFlowStep, useTranslation } from '@helios/frontend-core';

import { TakeoverProps } from '@palmetto/dsm-components';
import { kebabCase } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useUser } from '../../../api/user/useUser';
import UtilityArcadiaIframeStep from './Steps/UtilityArcadiaIframeStep';
import UtilityConnectionCompleted from './Steps/UtilityConnectionCompleted';
import UtilityLogin from './Steps/UtilityLogin';
import UtilityNotSupportedStep from './Steps/UtilityNotSupportedStep';
import UtilitySelectionStep from './Steps/UtilitySelectionStep';

type ConnectUtilityStepNames =
  | 'choose-utility'
  | 'utility-not-supported'
  | 'connect-utility-login'
  | 'utility-mfa-refresh'
  | 'utility-connect-completed'
  | 'utility-credential-update';

const INITIAL_STEP = 'choose-utility';
const headingPrefix = 'connectUtilityTakeover.takeover-heading';

const ConnectUtilityTakeover = (props: {
  isOpen: boolean;
  onDismiss: () => void;
  propertyId?: string;
  providerId?: string;
  connectedAccountId?: string;
  mfaRefreshUrl?: string;
  credentialUpdateUrl?: string;
  mfaSessionReset?: () => void;
  credentialSessionReset?: () => void;
}) => {
  const [headerDetails, setHeaderDetails] = useState<Partial<TakeoverProps>>({});

  const { isOpen, onDismiss } = props;
  const { user } = useUser();
  const { t } = useTranslation();
  const TRACKING_ID = `connect-utility-takeover`;

  const propertyId = user?.propertyIds?.[0];

  const stepsMap: StepComponent<ConnectUtilityStepNames>[] = useMemo(
    () => [
      {
        stepName: 'utility-mfa-refresh',
        component: UtilityArcadiaIframeStep,
        props: {
          setHeaderDetails,
          onDismiss,
          iframeUrl: props.mfaRefreshUrl,
          eventTrackingName: 'utility-mfa-refresh-completed',
          headingKey: 'connectUtilityTakeover.takeover-heading.connecting-account',
          propertyId: props.propertyId,
          providerId: props.providerId,
          connectedAccountId: props.connectedAccountId,
        },
      },
      {
        stepName: 'utility-credential-update',
        component: UtilityArcadiaIframeStep,
        props: {
          setHeaderDetails,
          onDismiss,
          iframeUrl: props.credentialUpdateUrl,
          eventTrackingName: 'utility-credential-update-completed',
          propertyId: props.propertyId,
          providerId: props.providerId,
          connectedAccountId: props.connectedAccountId,
        },
      },
      {
        stepName: 'choose-utility',
        component: UtilitySelectionStep,
        props: {
          propertyId,
        },
      },
      {
        stepName: 'utility-not-supported',
        component: UtilityNotSupportedStep,
        props: {
          onDismiss,
        },
      },
      {
        stepName: 'connect-utility-login',
        component: UtilityLogin,
        props: {
          setHeaderDetails,
          onDismiss,
        },
      },
      {
        stepName: 'utility-connect-completed',
        component: UtilityConnectionCompleted,
        props: {
          propertyId,
          setHeaderDetails,
        },
      },
    ],
    [],
  );

  const { activeStepContext, setStep } = useFlowStep(stepsMap, INITIAL_STEP, {
    onComplete: onDismiss,
  });

  const utilityTakeoverDismiss = () => {
    setStep(INITIAL_STEP);
    onDismiss();
  };

  const stepTopNavConfigMap: Record<ConnectUtilityStepNames, Partial<TakeoverProps>> = {
    'choose-utility': {
      heading: t(`${headingPrefix}.choose-utility`),
      topNavProps: {
        leftButton: {
          onClick: onDismiss,
          text: t('common.cancel'),
        },
      },
    },
    'utility-not-supported': {
      heading: t(`${headingPrefix}.choose-utility`),
      topNavProps: {
        leftButton: {
          onClick: onDismiss,
          text: t('common.cancel'),
        },
      },
    },
    'connect-utility-login': {
      heading: t(`${headingPrefix}.connect-account`),
      topNavProps: {
        leftButton: {
          onClick: () => setStep('choose-utility', {}, 'backward'),
          text: t('connectUtilityTakeover.utility'),
          icon: 'chevronLeft',
        },
      },
    },
    'utility-mfa-refresh': {
      heading: t(`${headingPrefix}.connect-account`),
      topNavProps: {
        leftButton: {
          onClick: onDismiss,
          text: t('common.cancel'),
        },
      },
    },
    'utility-credential-update': {
      heading: t(`${headingPrefix}.connect-account`),
      topNavProps: {
        leftButton: {
          onClick: onDismiss,
          text: t('common.cancel'),
        },
      },
    },
    'utility-connect-completed': {
      heading: t(`${headingPrefix}.connect-utility`),
      topNavProps: {
        leftButton: {
          onClick: onDismiss,
          text: t('common.cancel'),
        },
      },
    },
  };

  const memoizedStep = useMemo(() => {
    trackEvent('component loaded', {
      trackingId: `${TRACKING_ID}-${kebabCase(activeStepContext.stepName)}-step`,
    });
    return <activeStepContext.component />;
  }, [activeStepContext.stepName, TRACKING_ID]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (props.mfaRefreshUrl) {
      setStep('utility-mfa-refresh', {
        propertyId: props.propertyId,
        providerId: props.providerId,
        mfaRefreshUrl: props.mfaRefreshUrl,
      });
    } else if (props.credentialUpdateUrl) {
      setStep('utility-credential-update', {
        propertyId: props.propertyId,
        providerId: props.providerId,
        credentialUpdateUrl: props.credentialUpdateUrl,
      });
    } else if (props.propertyId && props.providerId) {
      setStep('connect-utility-login', { propertyId: props.propertyId, providerId: props.providerId });
    }
  }, [isOpen, props.mfaRefreshUrl, props.credentialUpdateUrl, props.propertyId, props.providerId]);
  useEffect(() => {
    setHeaderDetails(stepTopNavConfigMap[activeStepContext.stepName]);
  }, [activeStepContext.stepName]);

  return (
    <Takeover isOpen={isOpen} onClose={utilityTakeoverDismiss} trackingId={TRACKING_ID} {...headerDetails}>
      <div className="flex flex-col h-full overflow-scroll">
        <HorizontalSlideInOut direction={activeStepContext.direction || 'forward'}>
          {memoizedStep}
        </HorizontalSlideInOut>
      </div>
    </Takeover>
  );
};
export default ConnectUtilityTakeover;
